import type { ReactNode } from "react";
import { QuestionBody } from "@/components/marketing/QuestionBody";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { Btn, Eyebrow, SectionHeader } from "@/components/marketing/primitives";
import { ContactCTA } from "@/components/marketing/ContactCTA";
import { CredentialStrip } from "@/components/marketing/CredentialStrip";
import { INDUSTRIES } from "@/content/industries";
import { RELATED_INDUSTRIES } from "@/content/relatedIndustries";
import { FAQSection } from "@/components/marketing/FAQSection";
import { CrossLinks } from "@/components/marketing/CrossLinks";
import { RelatedLinks } from "@/components/marketing/RelatedLinks";
import { PostLinkLine } from "@/components/marketing/PostLinkLine";
import { RecentWork } from "@/components/marketing/RecentWork";
import { RichProse } from "@/components/marketing/RichProse";
import { TREE_SETUP } from "@/content/pageUpgrades";
import { tradeWhoSection } from "@/content/whoRuns";
import { INDUSTRY_POST_LINKS } from "@/content/postlinks";
import { INDUSTRY_SERVICE_LINKS } from "@/content/crosslinks";
import { pushAuditStart } from "@/lib/analytics";

import { INDUSTRY_FAQS, faqJsonLd } from "@/content/faqs";
import { IndustryTrackingCensus } from "@/components/marketing/TrackingCensus";
import { breadcrumbJsonLd } from "@/content/schema";
import { OG_IMAGE, abs } from "@/content/siteConfig";


export const Route = createFileRoute("/industries/$industry")({
  loader: async ({ params }) => {
    const { INDUSTRIES } = await import("@/content/industries");
    const ind = INDUSTRIES.find((i) => i.slug === params.industry);
    if (!ind) throw notFound();
    return ind;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const title = loaderData.slug === "home-services"
      ? "Google Ads for Home Services Companies"
      : loaderData.slug === "construction"
        ? "Google Ads for Construction Companies"
        : loaderData.title
      ? loaderData.title
      : loaderData.h1Lead
        ? `${loaderData.h1Lead} ${loaderData.name}`
        : `${loaderData.name} Marketing: Paid Media`;

    return {
      meta: [
        { title },
        { name: "description", content: loaderData.metaDescription ?? loaderData.short },
        { property: "og:title", content: title },
        { property: "og:description", content: loaderData.metaDescription ?? loaderData.short },
        { property: "og:type", content: "website" },
        { property: "og:image", content: OG_IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loaderData.slug === "home-services" ? "Google Ads for Home Services" : loaderData.slug === "construction" ? "Google Ads for Construction" : title },
        { name: "twitter:description", content: loaderData.metaDescription ?? loaderData.short },
        { name: "twitter:image", content: OG_IMAGE },
        { property: "og:url", content: abs(`/industries/${loaderData.slug}`) },
      ],
      links: [
        { rel: "canonical", href: abs(`/industries/${loaderData.slug}`) },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Industries", path: "/industries" },
            { name: loaderData.name, path: `/industries/${loaderData.slug}` },
          ]),
        },
        ...(INDUSTRY_FAQS[loaderData.slug]
          ? [
              {
                type: "application/ld+json",
                children: faqJsonLd(INDUSTRY_FAQS[loaderData.slug]!),
              },
            ]
          : []),
      ],
    };
  },
  component: IndustryDetail,
  notFoundComponent: () => (
    <SiteShell>
      <PageHero eyebrow="Industry" lede="That industry page doesn't exist.">
        Not <span className="display-italic">found.</span>
      </PageHero>
    </SiteShell>
  ),
});

function IndustryDetail() {
  const ind = Route.useLoaderData();
  return (
    <SiteShell>
      <PageHero
        eyebrow={ind.name}
        lede={ind.short}
        ctas={
          <>
            <Btn to="/audit" onClick={() => pushAuditStart("inline")}>
              Get your free audit →
            </Btn>
            <Btn to="/industries" variant="ghost">
              All industries
            </Btn>
          </>
        }

      >
        {ind.h1Lead ?? ind.name}{" "}
        <span className="display-italic">{ind.h1Italic ?? "marketing."}</span>
      </PageHero>

      {tradeWhoSection(ind.slug) ? <RichProse sections={[tradeWhoSection(ind.slug)!]} /> : null}

      <IndustryTrackingCensus industry={ind.slug} />

      <section className="bhg-section">
        <div className="wrap section-eyebrow-only">
          <div className="eyebrow-lead">
            <Eyebrow>Our approach</Eyebrow>
            <p
              className="prose-lead"
              style={{
                fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                fontWeight: 300,
                fontSize: "clamp(20px, 2.2vw, 28px)",
                lineHeight: 1.35,
                color: "#002FA7",
                margin: 0,
                maxWidth: "52ch",
              }}
            >
              {ind.thesis}
            </p>
          </div>
          <div className="mt-8">
            <PostLinkLine link={INDUSTRY_POST_LINKS[ind.slug]} />
          </div>
          {ind.evidence ? (
            <p
              className="mt-6"
              style={{
                color: "rgba(0,47,167,0.74)",
                fontSize: 15.5,
                lineHeight: 1.6,
                maxWidth: "62ch",
                margin: "24px 0 0",
              }}
            >
              {renderRich(ind.evidence)}
            </p>
          ) : null}
        </div>
      </section>

      {ind.deepDive && ind.deepDive.length > 0 ? (
        <section className="bhg-section">
          <div className="wrap">
            {ind.deepDive.map((block) => (
              <div key={block.heading} className="section-split prose-split mb-12 last:mb-0">
                <h2 className="section-heading"
                  style={{
                    fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                    fontWeight: 300,
                    fontSize: "clamp(22px, 2vw, 26px)",
                    letterSpacing: "-0.01em",
                    color: "#002FA7",
                    margin: 0,
                  }}
                >
                  {block.heading}
                </h2>
                <div className="section-body"><QuestionBody>{block.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className="mt-5"
                    style={{
                      fontSize: 16.5,
                      lineHeight: 1.6,
                      color: "rgba(0,47,167,0.78)",
                      maxWidth: "62ch",
                      margin: i === 0 ? undefined : "20px 0 0",
                    }}
                  >
                    {renderRich(p)}
                  </p>
                ))}</QuestionBody></div>
              </div>
            ))}
            <p
              className="mt-10 section-following"
              style={{
                fontSize: 16.5,
                lineHeight: 1.6,
                color: "rgba(0,47,167,0.78)",
                maxWidth: "62ch",
              }}
            >
              {["roofing","landscaping","pool-service","cleaning","plumbing","hvac","electricians"].includes(ind.slug) ? <>To see how we report, <Link to="/case-studies">read our case studies</Link>: platform leads and closed business, shown separately. Or <Link to="/contact">get in touch</Link> and we start with your tracking.</> : ["pest-control","remodeling","window-replacement","tree-services"].includes(ind.slug) ? <>Our reports show platform-reported leads and closed business separately: <Link to="/case-studies">read our case studies</Link>. When ready, <Link to="/contact">get in touch</Link> and we start with your current tracking.</> : ["concrete"].includes(ind.slug) ? <><Link to="/services/$service" params={{ service: "meta-ads" }} style={{ color: "var(--color-bleu)", textDecoration: "underline", textUnderlineOffset: 3 }}>Meta Ads</Link> show finished decorative and residential work to nearby homeowners. Our reports show platform-reported leads and closed business separately: <Link to="/case-studies">read our case studies</Link>. When ready, <Link to="/contact">get in touch</Link> and we start with your current tracking.</> : <>To see results, <Link to="/case-studies">read our case studies</Link>, which show platform-reported leads and closed business separately, or <Link to="/contact">get in touch</Link> and we will start with your current tracking.</>}
            </p>
          </div>
        </section>
      ) : null}

      {ind.slug === "tree-services" ? <RichProse sections={[TREE_SETUP]} /> : null}
      <section className="bhg-section">
        <div className="wrap">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <SectionHeader
                num={ind.channelEyebrow ?? "Channels we run"}
                title={ind.channelTitle ?? <>For <span className="display-italic">{ind.name.toLowerCase()}.</span></>}
              />
              <ul
                className="mt-8 list-none p-0"
                style={{ borderTop: "1px solid rgba(0,47,167,0.26)" }}
              >
                {ind.channels.map((c: string) => (
                  <li
                    key={c}
                    className="mono"
                    style={{
                      padding: "16px 0",
                      borderBottom: "1px solid rgba(0,47,167,0.12)",
                      fontSize: 13,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#002FA7",
                    }}
                  >
                    → {c}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SectionHeader
                num={ind.buyerEyebrow ?? "What we tune for"}
                title={ind.buyerTitle ?? <>Built-in <span className="display-italic">specifics.</span></>}
              />
              <ul
                className="mt-8 list-none p-0"
                style={{ borderTop: "1px solid rgba(0,47,167,0.26)" }}
              >
                {ind.buyerNotes.map((n: string) => (
                  <li
                    key={n}
                    style={{
                      padding: "16px 0",
                      borderBottom: "1px solid rgba(0,47,167,0.12)",
                      color: "rgba(0,47,167,0.85)",
                      fontSize: 15,
                      lineHeight: 1.55,
                    }}
                  >
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CrossLinks
        items={INDUSTRY_SERVICE_LINKS[ind.slug] ?? []}
        num={ind.crossLinksEyebrow ?? "Where to go next"}
        title={ind.crossLinksTitle ?? <>Services for <span className="display-italic">{ind.name.toLowerCase()}.</span></>}
      />

      {INDUSTRY_FAQS[ind.slug] ? (
        <FAQSection
          items={INDUSTRY_FAQS[ind.slug]!}
          num={ind.faqEyebrow ?? "FAQ"}
          title={ind.faqTitle ?? <>{ind.name} <span className="display-italic">questions.</span></>}
        />
      ) : null}

      <CredentialStrip />

      <RelatedLinks
        title="Related industries"
        compact
        items={(RELATED_INDUSTRIES[ind.slug] ?? []).flatMap((slug) => {
          const i = INDUSTRIES.find((industry) => industry.slug === slug);
          return i ? [{
          href: `/industries/${i.slug}`,
          label: i.name,
          }] : [];
        })}
      />

      {ind.slug === "tree-services" ? <RecentWork /> : null}
      <ContactCTA />
    </SiteShell>
  );
}

/** Renders [label](/path) markers inside deep-dive prose as router links. */
function renderRich(text: string) {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1]!;
    const path = m[2]!;
    const style = { color: "#002FA7", textDecoration: "underline", textUnderlineOffset: 3 };
    if (path.startsWith("/blog/")) {
      parts.push(
        <Link key={parts.length} to="/blog/$slug" params={{ slug: path.slice(6) }} style={style}>
          {label}
        </Link>,
      );
    } else if (path === "/services/local-services-ads-for-roofers") {
      parts.push(
        <Link key={parts.length} to="/services/local-services-ads-for-roofers" style={style}>
          {label}
        </Link>,
      );
    } else if (path.startsWith("/services/")) {
      parts.push(
        <Link key={parts.length} to="/services/$service" params={{ service: path.slice(10) }} style={style}>
          {label}
        </Link>,
      );
    } else if (path === "/research/conversion-tracking-report") {
      parts.push(
        <Link key={parts.length} to="/research/conversion-tracking-report" style={style}>
          {label}
        </Link>,
      );
    } else if (path === "/audit") {
      parts.push(
        <Link key={parts.length} to="/audit" style={style}>
          {label}
        </Link>,
      );
    } else {
      parts.push(label);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
